import React, { useState, useEffect } from "react";
import AddInputs from "../../../../components/Add/AddInputs";
import Image from "../../../../components/images/Image";
import useApi from "../../../../services/useApi";
import { useDispatch } from "react-redux";
import { setMNOList, setMNOError, setMNOLoading } from "../addMDSlice";

interface PostpaidDetail {
    mno_id: string;
    mno_name: string;
    bundle_dispersive_fee_percent: number | string;
    etopup_dispersive_fee_percent: number | string;
    child_bundle_dispersive_fee_percent?: number | string;
    child_etopup_dispersive_fee_percent?: number | string;
    status: boolean;
}

interface MNOOption {
    id: string;
    name: string;
    etopup_dispersive_fee_percent: string;
    bundle_dispersive_fee_percent: string;
}

interface PostpaidServiceFeeFieldsProps {
    service_fee_details: PostpaidDetail[];
    parentMDSelected: boolean;
    type: string;
    onServiceFeeDetailsChange: (details: PostpaidDetail[]) => void;
    onErrorsChange: (errors: { [key: string]: string }) => void;
    localErrors: { [key: string]: string };
    childPostpaidPercentage: {
        [key: number]: {
            child_bundle_dispersive_fee_percent: string | number;
            child_etopup_dispersive_fee_percent: string | number;
        };
    };
    onChildPostpaidPercentageChange: (percentages: {
        [key: number]: {
            child_bundle_dispersive_fee_percent: string | number;
            child_etopup_dispersive_fee_percent: string | number;
        };
    }) => void;
    childPostpaidErrors: {
        [key: number]: {
            child_bundle_dispersive_fee_percent?: string;
            child_etopup_dispersive_fee_percent?: string;
        };
    };
    onChildPostpaidErrorsChange: (errors: {
        [key: number]: {
            child_bundle_dispersive_fee_percent?: string;
            child_etopup_dispersive_fee_percent?: string;
        };
    }) => void;
    onMnoListChange?: (mnoList: MNOOption[]) => void;
}

const PostpaidServiceFeeFields: React.FC<PostpaidServiceFeeFieldsProps> = ({
    service_fee_details,
    parentMDSelected,
    type,
    onServiceFeeDetailsChange,
    onErrorsChange,
    localErrors,
    childPostpaidPercentage,
    onChildPostpaidPercentageChange,
    childPostpaidErrors,
    onChildPostpaidErrorsChange,
    onMnoListChange
}) => {
    const dispatch = useDispatch();
    const { getAPI } = useApi();
    const [mnoPage, setMnoPage] = useState(1);
    const [mnoSearch, setMnoSearch] = useState("");
    const [hasMoreMnos, setHasMoreMnos] = useState(true);
    const [mnoList, setMnoList] = useState<MNOOption[]>([]);
    const [selectedMNOs, setSelectedMNOs] = useState<{ [key: string]: { etopupPercent: number, bundlePercent: number } }>({});
    const [mnoLoading, setMnoLoading] = useState(false);

    const handleMnos = async (page = 1, search = "") => {
        if (mnoLoading) return;
        setMnoLoading(true);
        try {
            let url = `mnos?deployment_status=DEPLOYED&page_number=${page}`;
            const response = await getAPI(url);
            if (!response.error) {
                const newMnos = response.data.data.map((mno: any) => ({
                    id: mno.id,
                    name: mno.name,
                    etopup_dispersive_fee_percent: Number(mno.etopup_dispersive_fee_percent),
                    bundle_dispersive_fee_percent: Number(mno.bundle_dispersive_fee_percent)
                }));

                const updatedMnoList = page === 1 ? newMnos : [...mnoList, ...newMnos];
                setMnoList(updatedMnoList);

                dispatch(setMNOList({
                    data: updatedMnoList,
                    maxUpfrontServiceFeePercent: response.data.max_upfront_service_fee_percent || 0
                }));

                const totalCount = parseInt(response.data.total_count);
                const currentCount = page === 1 ? newMnos.length : mnoList.length + newMnos.length;
                setHasMoreMnos(currentCount < totalCount);
                setMnoPage(page);
            }
        } catch (error) {
            console.error('Error fetching MNOs:', error);
            dispatch(setMNOError('Failed to fetch MNOs'));
        } finally {
            setMnoLoading(false);
            dispatch(setMNOLoading(false));
        }
    };

    useEffect(() => {
        handleMnos(1, "");
    }, []);

    useEffect(() => {
        if (onMnoListChange) {
            onMnoListChange(mnoList);
        }
    }, [mnoList, onMnoListChange]);

    const loadMoreMnoOptions = () => {
        if (hasMoreMnos && !mnoLoading) {
            handleMnos(mnoPage + 1, mnoSearch);
        }
    };

    const handleMnoSearch = (search: string) => {
        setMnoSearch(search);
        setMnoPage(1);
        handleMnos(1, search);
    };

    const handleAddPostpaidRow = () => {
        const newDetails = [...service_fee_details, {
            mno_id: '',
            mno_name: '',
            bundle_dispersive_fee_percent: '',
            etopup_dispersive_fee_percent: '',
            status: true
        }];

        onServiceFeeDetailsChange(newDetails);
        onErrorsChange({});
    };

    useEffect(() => {
        if (service_fee_details.length === 0 && !parentMDSelected) {
            handleAddPostpaidRow();
        }
    }, []);

    const handlePostpaidInputChange = (index: number, id: string, value: string | number | boolean) => {
        if (id === `mno_id`) {
            const selectedMno = mnoList.find(mno => mno.name === value);
            if (selectedMno) {
                const updatedDetails = [...service_fee_details];
                updatedDetails[index] = {
                    ...updatedDetails[index],
                    mno_id: selectedMno.id,
                    bundle_dispersive_fee_percent: '',
                    etopup_dispersive_fee_percent: ''
                };

                onServiceFeeDetailsChange(updatedDetails);

                onChildPostpaidPercentageChange({
                    ...childPostpaidPercentage,
                    [index]: {
                        child_bundle_dispersive_fee_percent: '',
                        child_etopup_dispersive_fee_percent: ''
                    }
                });

                onChildPostpaidErrorsChange({
                    ...childPostpaidErrors,
                    [index]: {}
                });

                const newErrors = { ...localErrors };
                delete newErrors[`postpaid_${index}_mno_id`];
                delete newErrors[`postpaid_${index}_bundle`];
                delete newErrors[`postpaid_${index}_etopup`];
                onErrorsChange(newErrors);

                setSelectedMNOs(prev => ({
                    ...prev,
                    [selectedMno.id]: {
                        etopupPercent: Number(selectedMno.etopup_dispersive_fee_percent),
                        bundlePercent: Number(selectedMno.bundle_dispersive_fee_percent)
                    }
                }));
            }
            return;
        }

        if (id === 'bundle_dispersive_fee_percent' || id === 'etopup_dispersive_fee_percent') {
            if (value === '' || value === '-' || value === '.' || String(value).endsWith('.') || value === '0') {
                const updatedDetails = [...service_fee_details];
                updatedDetails[index] = {
                    ...updatedDetails[index],
                    [id]: value
                };

                onServiceFeeDetailsChange(updatedDetails);
                const fieldErrorKey = `postpaid_${index}_${id === 'bundle_dispersive_fee_percent' ? 'bundle' : 'etopup'}`;
                const newErrors = { ...localErrors };
                delete newErrors[fieldErrorKey];
                onErrorsChange(newErrors);
                return;
            }

            if (!isNaN(Number(value))) {
                const updatedDetails = [...service_fee_details];
                updatedDetails[index] = {
                    ...updatedDetails[index],
                    [id]: value
                };

                onServiceFeeDetailsChange(updatedDetails);

                const fieldErrorKey = `postpaid_${index}_${id === 'bundle_dispersive_fee_percent' ? 'bundle' : 'etopup'}`;
                const newErrors = { ...localErrors };
                delete newErrors[fieldErrorKey];
                onErrorsChange(newErrors);
            } else {
                const fieldErrorKey = `postpaid_${index}_${id === 'bundle_dispersive_fee_percent' ? 'bundle' : 'etopup'}`;
                onErrorsChange({
                    ...localErrors,
                    [fieldErrorKey]: "Please enter a valid percentage"
                });
            }
            return;
        }

        if (id === 'status') {
            const updatedDetails = [...service_fee_details];
            updatedDetails[index] = {
                ...updatedDetails[index],
                status: value as boolean
            };

            onServiceFeeDetailsChange(updatedDetails);
            return;
        }

        const updatedDetails = [...service_fee_details];
        updatedDetails[index] = {
            ...updatedDetails[index],
            [id]: value
        };

        onServiceFeeDetailsChange(updatedDetails);

        const newErrors = { ...localErrors };
        delete newErrors[`postpaid_${index}_${id}`];
        onErrorsChange(newErrors);
    };

    const handleDelete = (index: number) => {
        const mnoId = service_fee_details[index].mno_id;

        if (mnoId) {
            const newSelectedMNOs = { ...selectedMNOs };
            delete newSelectedMNOs[mnoId];
            setSelectedMNOs(newSelectedMNOs);
        }

        const updatedDetails = service_fee_details.filter((_, i) => i !== index);
        onServiceFeeDetailsChange(updatedDetails);

        const newErrors = { ...localErrors };
        Object.keys(newErrors).forEach(key => {
            if (key.startsWith(`postpaid_${index}_`)) {
                delete newErrors[key];
            }
        });
        onErrorsChange(newErrors);
    };

    const handleChildPostpaid = (id: string, value: string | number, idx: number) => {
        const updatedDetails = [...service_fee_details];
        updatedDetails[idx] = {
            ...updatedDetails[idx],
            [id]: value
        };

        onServiceFeeDetailsChange(updatedDetails);

        onChildPostpaidErrorsChange(prev => {
            const newErrors = { ...prev };
            if (newErrors[idx]) {
                delete newErrors[idx][id];
                if (Object.keys(newErrors[idx]).length === 0) {
                    delete newErrors[idx];
                }
            }
            return newErrors;
        });
    };

    const getPostpaidInputs = (row: any, index: number) => {
        const selectedMnoNames = service_fee_details
            .filter((_, i) => i !== index)
            .map(detail => {
                const mno = mnoList.find(m => m.id === detail.mno_id);
                return mno ? mno.name : '';
            });

        const availableMnos = mnoList.filter(mno => !selectedMnoNames.includes(mno.name));

        const selectedMno = selectedMNOs[row.mno_id];
        const maxBundlePercent = selectedMno ? selectedMno.bundlePercent : 0;
        const maxEtopupPercent = selectedMno ? selectedMno.etopupPercent : 0;

        return {
            "": {
                "Telecom": {
                    id: `mno_id`,
                    type: "dropDown",
                    required: true,
                    disabled: parentMDSelected ? true : false,
                    noSearch: true,
                    options: availableMnos.map(mno => mno.name),
                    tabIndex: 0,
                    loadMoreOptions: loadMoreMnoOptions,
                    onSearch: handleMnoSearch,
                    hasMore: hasMoreMnos,
                    loading: mnoLoading
                },
                [(parentMDSelected && type !== 'Master Distributors') ? "Parent eTop Up Service Fee Percentage" : "eTop Up Service Fee Percentage"]: {
                    required: true,
                    disabled: (parentMDSelected && type !== 'Master Distributors') ? true : false,
                    id: "etopup_dispersive_fee_percent",
                    type: "numberInput",
                    maxLength: 60,
                    decimalPlaces: 2,
                    tabIndex: 0,
                    min: 0,
                    max: 100,
                    percentage: true,
                    placeholder: 'Enter percentage',
                    ...((!parentMDSelected) && {
                        secondaryText: service_fee_details[index].mno_id ?
                            `Note: Percentage must not exceed ${mnoList.find(mno => mno.id === service_fee_details[index].mno_id)?.etopup_dispersive_fee_percent}%` :
                            undefined,
                    })
                },
                [(parentMDSelected && type !== 'Master Distributors') ? "Parent Bundle Activation Service Fee Percentage" : "Bundle Activation Service Fee Percentage"]: {
                    required: true,
                    disabled: (parentMDSelected && type !== 'Master Distributors') ? true : false,
                    id: "bundle_dispersive_fee_percent",
                    type: "numberInput",
                    maxLength: 60,
                    tabIndex: 0,
                    min: 0,
                    decimalPlaces: 2,
                    max: 100,
                    percentage: true,
                    placeholder: 'Enter percentage',
                    ...((!parentMDSelected) && {
                        secondaryText: service_fee_details[index].mno_id ?
                            `Note: Percentage must not exceed ${mnoList.find(mno => mno.id === service_fee_details[index].mno_id)?.bundle_dispersive_fee_percent}%` :
                            undefined,
                    })
                }
            }
        };
    };

    const childPostpaidInputs = (row: any, index: number) => {
        return {
            "": {
                "Child eTop Up Service Fee Percentage": {
                    required: true,
                    disabled: false,
                    id: "child_etopup_dispersive_fee_percent",
                    type: "numberInput",
                    maxLength: 60,
                    decimalPlaces: 2,
                    tabIndex: 0,
                    min: 0,
                    max: 100,
                    percentage: true,
                    placeholder: 'Enter percentage',
                    secondaryText: `Note: Percentage must not exceed ${row.etopup_dispersive_fee_percent}%`
                },
                "Child Bundle Activation Service Fee Percentage": {
                    required: true,
                    disabled: false,
                    id: "child_bundle_dispersive_fee_percent",
                    type: "numberInput",
                    maxLength: 60,
                    tabIndex: 0,
                    min: 0,
                    decimalPlaces: 2,
                    max: 100,
                    percentage: true,
                    placeholder: 'Enter percentage',
                    secondaryText: `Note: Percentage must not exceed ${row.bundle_dispersive_fee_percent}%`
                }
            }
        }
    };

    return (
        <>
            {service_fee_details.map((row, idx) => {
                const selectedMnoName = mnoList.find((mno) => mno.id === row.mno_id)?.name || "";
                const inputValues = { ...row, mno_id: selectedMnoName };
                return (
                    <div key={idx} className="bg-blue-50 p-4 rounded-lg mt-5 mb-4">
                        <div className="flex flex-col gap-4 items-start">
                            <div className="w-full">
                                <AddInputs
                                    inputs={getPostpaidInputs(row, idx)}
                                    totalInputCount={1}
                                    inputValues={inputValues}
                                    onInputChange={(id, value) => handlePostpaidInputChange(idx, id, value)}
                                    mapping={true}
                                    loadMoreOptions={loadMoreMnoOptions}
                                    errors={{
                                        mno_id: localErrors[`postpaid_${idx}_mno_id`] || "",
                                        etopup_dispersive_fee_percent: localErrors[`postpaid_${idx}_etopup`] || "",
                                        bundle_dispersive_fee_percent: localErrors[`postpaid_${idx}_bundle`] || "",
                                    }}
                                />
                            </div>
                            {parentMDSelected && type !== "Master Distributors" && (
                                <div className="w-full">
                                    <AddInputs
                                        inputs={childPostpaidInputs(row, idx)}
                                        totalInputCount={1}
                                        inputValues={{
                                            child_etopup_dispersive_fee_percent: service_fee_details[idx]?.child_etopup_dispersive_fee_percent === 0 ? 0 : service_fee_details[idx]?.child_etopup_dispersive_fee_percent ? service_fee_details[idx]?.child_etopup_dispersive_fee_percent : "",
                                            child_bundle_dispersive_fee_percent: service_fee_details[idx]?.child_bundle_dispersive_fee_percent === 0 ? 0 : service_fee_details[idx]?.child_bundle_dispersive_fee_percent ? service_fee_details[idx]?.child_bundle_dispersive_fee_percent : "",
                                        }}
                                        onInputChange={(id, value) => handleChildPostpaid(id, value, idx)}
                                        mapping={true}
                                        errors={{
                                            child_etopup_dispersive_fee_percent: childPostpaidErrors[idx]?.child_etopup_dispersive_fee_percent || "",
                                            child_bundle_dispersive_fee_percent: childPostpaidErrors[idx]?.child_bundle_dispersive_fee_percent || "",
                                        }}
                                    />
                                </div>
                            )}
                            <div className="flex flex-row w-full justify-between items-end">
                                <div className="flex flex-col items-start">
                                    <label className="block font-medium mb-3">Status</label>
                                    <div className="status custom-toggle flex items-center relative">
                                        <label className="switch">
                                            <input
                                                type="checkbox"
                                                checked={row.status}
                                                onChange={(e) => handlePostpaidInputChange(idx, "status", e.target.checked)}
                                            />
                                            <span className="slider round"></span>
                                        </label>
                                        <p className={`disable_switch ml-3 -mt-1`}>
                                            {row.status ? "Active" : "Inactive"}
                                        </p>
                                    </div>
                                </div>
                                {(service_fee_details.length > 1 && !parentMDSelected) && (
                                    <Image
                                        src={"delete_blue"}
                                        className="cursor-pointer"
                                        onClick={() => handleDelete(idx)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
            {!parentMDSelected && (
                <button
                    type="button"
                    className="text-p_primary font-medium flex items-center gap-1"
                    data-testid="add_service_detail"
                    onClick={handleAddPostpaidRow}
                >
                    <span className=" text-2xl">+</span>Add
                </button>
            )}
        </>
    );
};

export default PostpaidServiceFeeFields; 